import React, { useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ChatPanel } from '@/components';
import { usePostMessage } from '@/hooks';
import type { InboundInit } from '@/types/bridge';
import { setCommon, setUserInfo } from '@/common';
import { setLanguage } from '@/i18n';
import { useChatStore } from '@/store';
import { ESolutionCode } from './types';
import { isWidgetMode } from '@/utils/widgetMode';
import { readDevInitFromQuery } from '@/utils/devInit';
import { applyInitPayload } from '@/utils/applyInitPayload';
import WidgetApp from './WidgetApp';

import './styles/global.less';
import './styles/widget.less';
import '../bootstrap/iconfont/iconfont.css';

const PanelApp: React.FC = () => {
  const [config, setConfig] = useState<InboundInit['payload']['config']>({});
  const [isReady, setIsReady] = useState(false);
  const initRoles = useChatStore((s) => s.initRoles);
  const { send } = usePostMessage({
    onInit: useCallback(
      (payload: InboundInit['payload']) => {
        const solutionCode = payload?.solution_code as string;
        setConfig(payload.config || {});
        // 与 widget / npm 入口共用同一套写入逻辑，避免新增字段时只改了一处
        applyInitPayload(payload);
        void initRoles(solutionCode)
          .catch((err) => {
            console.error('[initRoles] failed:', err);
          })
          .finally(() => {
            setIsReady(true);
          });
      },
      [initRoles],
    ),
  });

  const handleClose = useCallback(() => {
    send({ type: 'close' });
  }, [send]);

  const handleFullScreen = useCallback(() => {
    send({ type: 'full-screen' });
  }, [send]);

  /**
   * 本地联调兜底：直接打开 http://localhost:3000/?debug=1 时没有父页面发 init，
   * 这里改用 URL query 初始化。生产构建下 readDevInitFromQuery() 恒返回 null。
   */
  useEffect(() => {
    const devPayload = readDevInitFromQuery();
    if (!devPayload) return;

    let cancelled = false;
    setConfig(devPayload.config || {});
    applyInitPayload(devPayload);

    void initRoles(devPayload.solution_code as string)
      .catch((err) => {
        console.error('[initRoles] failed:', err);
      })
      .finally(() => {
        if (!cancelled) setIsReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [initRoles]);

  // ========================  MOCK-start ========================
  // useEffect(() => {
  //   let cancelled = false;
  //   const solutionCode = ESolutionCode.BOSS_REPORT;
  //   setCommon(
  //     'token',
  //     'eyJ6aXAiOiJERUYiLCJhbGciOiJFUzI1NiJ9.eJwdjEEOgjAQRe8yaxbttNNOOYQrLlBbaiCgWJCYGO_ihlt4He5hZfXzX17eC_qlgxrQeR-DYRWc0Ciss5Ysy4iaiBQnqKB9TlBLy0zMRukKOr8cgJwzB_BTMSD4qKLSUvmQNJN0OnE4R2ox2tB6WVKPORfRoCGN5c7LBWpRwS2XhebUIEqh5DpMYz-4YNexFz7dBxVN7kxy_0K-FnX_fvZtg_cPJlc4Wg.8Wa3Rgmg90uJoPGhNXdvo6vlZWWaF251282f_CNHPWL33nkhzMWkY81oQkWPeYUweoINKcnOFxO083nfjUgfwg',
  //   );
  //   setCommon('storeId', 'S002501211415225500005615');
  //   setCommon('tenantCode', 'TNT221031vlpmjl9c7vmj0afql3d6ri6f9');
  //   const mockLang = 'zh-CN';
  //   setCommon('language', mockLang);
  //   setLanguage(mockLang);
  //   setCommon('bizType', 'restaurant');
  //   setCommon('solution_code', solutionCode);
  //   setUserInfo('ye.tao@sunmi.com', '373275', '18937107705');
  //   useChatStore.getState().syncHistoryScope();
  //   initGoogleAnalytics();
  //   void initRoles(solutionCode)
  //     .catch((err) => {
  //       console.error('[initRoles] failed:', err);
  //     })
  //     .finally(() => {
  //       if (!cancelled) {
  //         setIsReady(true);
  //       }
  //     });

  //   return () => {
  //     cancelled = true;
  //   };
  // }, [initRoles]);
  // ========================  MOCK-end  ========================

  return (
    <ChatPanel
      config={config}
      onClose={handleClose}
      isReady={isReady}
      onFullScreen={handleFullScreen}
    />
  );
};

const widgetMode = isWidgetMode();
if (widgetMode) {
  document.documentElement.classList.add('widget-mode');
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root not found');

const root = createRoot(container);
root.render(
  <React.StrictMode>
    {widgetMode ? <WidgetApp /> : <PanelApp />}
  </React.StrictMode>,
);
